import { type VFC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { CardGridArea, Tag } from "widgets";

import { createSummaryCardsFromClassifications } from "../../app-utils/card.js";
import { getGeneTypeColor } from "../../app-utils/color.js";
import { useLoadDispatchWithIdAsNumber } from "../../app-utils/hooks/useLoadDispatchWithIdAsNumber.js";
import { type User } from "../../graphql.js";
import { type ITranscriptState, shouldLoadTranscript } from "../../redux/modules/transcript.js";
import { type IAppState } from "../../redux/reducer.js";
import { SEQUENCE_TYPE_LABEL } from "../../settings/settings.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import { type IPageHeaderProps } from "../core/navigation-panel/PageHeader.js";
import GenericEntitySummaryView from "./GenericEntitySummaryView.js";
import { DatabaseCard } from "./cards/DatabaseCard.js";
import { GeneCard } from "./cards/GeneCard.js";
import { GenesCard } from "./cards/GenesCard.js";
import { GenomeCard } from "./cards/GenomeCard.js";
import { IntReferencesCard } from "./cards/IntReferencesCard.js";
import { LocationCard } from "./cards/LocationCard.js";
import { ProteinListCard } from "./cards/ProteinListCard.js";
import { ReferencesCard } from "./cards/ReferencesCard.js";
import { RelatedProjectsCard } from "./cards/RelatedProjectsCard.js";
import { TranscriptCard } from "./cards/TranscriptCard.js";
import { renderTranscriptAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";

interface ITranscriptSummaryPageOwnProps {
  transcriptId?: number; // In case the path is not "/transcript/:transcriptId" @see BlastResultHitSummaryView
  title?: string;
  pageHeaderProps?: IPageHeaderProps;
  blastContext?: boolean;
}

const TranscriptSummaryView: VFC<ITranscriptSummaryPageOwnProps> = (props) => {
  const { transcriptId } = useParams<"transcriptId">();
  const { transcript, isLoading } = useSelector<IAppState, ITranscriptState>((state) => state.transcript);
  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadTranscript, props.transcriptId ?? transcriptId);

  useEffect(() => {
    void load();
  }, [load]);

  const showSpinner = isLoading || !transcript;
  const sequenceTypeLabel = transcript && SEQUENCE_TYPE_LABEL[transcript.sequenceTypeId];
  const tag = transcript && sequenceTypeLabel && (
    <Tag label={sequenceTypeLabel} intentColor={getGeneTypeColor(sequenceTypeLabel)} />
  );

  return (
    <GenericEntitySummaryView
      title={(props.title ? `${props.title}: ` : "") + (transcript ? transcript.sequenceId : "")}
      subtitle={transcript?.description ?? "Transcript Summary"}
      entity={isLoading ? null : transcript}
      headerTags={(props.pageHeaderProps?.tags || []).concat(tag)}
      project={props.pageHeaderProps?.project}
      headerAttributes={[
        <AttributesListWidget
          attributes={renderTranscriptAttributesWidgets(transcript)}
          key={"transcript-summary-view-widget-1"}
        />,
      ]}
      isLoading={showSpinner}
      getEntityName={() => transcript?.sequenceId}
    >
      {transcript && (
        <>
          <TranscriptCard transcript={transcript} area={CardGridArea.Left} blastContext={props.blastContext} />
          <GenomeCard genome={transcript.genome} area={CardGridArea.Left} />
          <DatabaseCard genome={transcript.genome} database={transcript.database} area={CardGridArea.Left} />
          {createSummaryCardsFromClassifications(transcript.allClassifications, CardGridArea.Left)}

          {transcript.genes && transcript.genes.length > 1 ? (
            <GenesCard genes={transcript.genes} area={CardGridArea.Right} title="Parent Genes" />
          ) : (
            <GeneCard gene={transcript.genes[0]} area={CardGridArea.Right} title="Parent Gene" />
          )}

          <ProteinListCard proteins={transcript.proteins} area={CardGridArea.Right} />
          <LocationCard transcript={transcript} area={CardGridArea.Right} />
          <RelatedProjectsCard
            relatedProjects={transcript.relatedProjects}
            currentUser={currentUser}
            area={CardGridArea.Panel}
          />
          <IntReferencesCard references={transcript.internalReferences} area={CardGridArea.Right} />
          <ReferencesCard
            title="Literature References"
            references={transcript.literatureReferences}
            area={CardGridArea.Right}
          />
          <ReferencesCard references={transcript.allExternalReferences} area={CardGridArea.Left} />
        </>
      )}
    </GenericEntitySummaryView>
  );
};

export default TranscriptSummaryView;
